// frontend/src/pages/AnnouncementsPage.js

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "api";
import { Preferences } from "@capacitor/preferences";
import BackButton from "../components/BackButton";
import { PencilSimple, Trash, Check, PushPin } from "phosphor-react";

/* ---------- Auth helpers ---------- */
async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}

  return localStorage.getItem("token") || sessionStorage.getItem("token") || null;
}

async function getAuthHeaders() {
  const t = await getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/* ---------- Shared text splitter ---------- */
function parseTitleBody(full) {
  const text = String(full || "").replace(/\r\n/g, "\n").trim();
  if (!text) return { title: "", body: "" };

  const idx = text.indexOf("\n");
  if (idx === -1) return { title: text, body: "" };

  const title = text.slice(0, idx).trim();
  const body = text.slice(idx + 1).trim();

  return { title, body };
}

export default function AnnouncementsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isAdmin, setIsAdmin] = useState(false);
  const [meId, setMeId] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [drafts, setDrafts] = useState({});

  const sorted = useMemo(() => {
    const list = Array.isArray(items) ? items.slice() : [];

    list.sort((a, b) => {
      const ap = a?.pinned ? 1 : 0;
      const bp = b?.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;

      const at = new Date(a?.created_at || 0).getTime();
      const bt = new Date(b?.created_at || 0).getTime();

      return bt - at;
    });

    return list;
  }, [items]);

  const fetchAll = async () => {
    setLoading(true);

    try {
      const headers = await getAuthHeaders();

      try {
        const meRes = await api.get("/users/me", { headers });
        const uid =
          meRes?.data?.id ?? Number(localStorage.getItem("user_id")) ?? null;

        if (uid != null) setMeId(uid);
      } catch {
        const uid = Number(localStorage.getItem("user_id"));
        if (Number.isFinite(uid)) setMeId(uid);
      }

      const res = await api.get(`/groups/${id}/announcements`, { headers });
      const list = Array.isArray(res?.data) ? res.data : [];
      setItems(list);

      const init = {};
      list.forEach((a) => {
        const { title, body } = parseTitleBody(a?.body || "");
        init[a.id] = { title, body, pinned: !!a?.pinned };
      });
      setDrafts(init);

      let adminFlag = false;

      try {
        const grp = await api.get(`/groups/${id}`, { headers });
        const g = grp?.data || {};

        adminFlag =
          Boolean(g.me_is_admin) ||
          Boolean(g.is_admin) ||
          Boolean(g.current_user_is_admin) ||
          false;
      } catch {}

      if (!adminFlag) {
        try {
          const adminsRes = await api.get(`/groups/${id}/admins`, { headers });
          const admins = Array.isArray(adminsRes?.data) ? adminsRes.data : [];
          const uid = Number(meId ?? localStorage.getItem("user_id"));

          if (uid != null) {
            adminFlag = admins.some((a) => String(a.id) === String(uid));
          }
        } catch {}
      }

      setIsAdmin(adminFlag);
    } catch {
      setItems([]);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!mounted) return;
      await fetchAll();
    })();

    return () => {
      mounted = false;
    };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (a) => {
    setEditingId(a.id);

    setDrafts((prev) => {
      if (prev[a.id]) return prev;

      const { title, body } = parseTitleBody(a?.body || "");
      return { ...prev, [a.id]: { title, body, pinned: !!a?.pinned } };
    });
  };

  const cancelEdit = () => setEditingId(null);

  const saveRow = async (a) => {
    const d = drafts[a.id] || { title: "", body: "", pinned: false };
    const title = (d.title || "").trim();
    const body = (d.body || "").trim();

    if (!title && !body) {
      alert("Please enter a title or message.");
      return;
    }

    const combined = title ? `${title}\n\n${body}`.trim() : body;

    try {
      setSavingId(a.id);

      const headers = await getAuthHeaders();

      await api.patch(
        `/groups/${id}/announcements/${a.id}`,
        { body: combined, pinned: !!d.pinned },
        { headers }
      );

      setSavingId(null);
      setEditingId(null);

      await fetchAll();
    } catch {
      setSavingId(null);
    }
  };

  const deleteRow = async (a) => {
    const ok = window.confirm("Delete this announcement? This can’t be undone.");
    if (!ok) return;

    try {
      setDeletingId(a.id);

      const headers = await getAuthHeaders();
      await api.delete(`/groups/${id}/announcements/${a.id}`, { headers });

      setDeletingId(null);

      await fetchAll();
    } catch {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-5"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 mx-auto px-4 flex items-center">
          <BackButton
            className="text-blue-600"
            ariaLabel="Back"
            onClick={() => navigate(-1)}
          />

          <h1 className="flex-1 text-left text-blue-600 font-bold leading-none tracking-[-0.01em] text-[20px] whitespace-nowrap">
            Announcements
          </h1>
        </div>
      </header>

      <main
        className="mx-auto bg-blue-50 px-4 pt-2"
      >
        <div className="rounded-full bg-transparent p-0">
          {loading ? (
            <div className="rounded-2xl border border-blue-100 bg-white/90 px-3 py-4 text-sm font-semibold text-blue-500">
              Loading…
            </div>
          ) : sorted.length === 0 ? (
            <div className="bg-transparent px-3 ml-2 text-sm font-semibold text-blue-800">
              No announcements yet
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map((a) => {
                const isEditing = editingId === a.id;
                const isSaving = savingId === a.id;
                const isDeleting = deletingId === a.id;

                const draft = drafts[a.id] || {
                  title: "",
                  body: "",
                  pinned: !!a?.pinned,
                };

                const { title, body } = isEditing
                  ? draft
                  : parseTitleBody(a?.body || "");

                return (
                  <div
                    key={a.id}
                    className="
                      rounded-2xl bg-white max-w-[97%] ml-2
                      px-4 pt-1 pb-3 shadow-[0_4px_0px_rgba(37,99,235,0.96)] transition
                    "
                  >
                    <div className="flex items-start justify-between ">
                      <div className="min-w-0 flex-1 flex items-center min-h-[36px]">
                        {!isEditing ? (
                          <div className="w-full">
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-blue-900 break-words">
                              {a?.pinned ? (
                                <PushPin
                                  size={15}
                                  weight="fill"
                                  className="shrink-0 text-blue-500"
                                />
                              ) : null}

                              <span>{title || "(No title)"}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full space-y-2">
                            <input
                              type="text"
                              className="
                                w-full border border-blue-100 rounded-2xl px-3 py-2
                                text-sm font-medium text-black
                                focus:outline-none focus:ring-2 focus:ring-blue-600
                              "
                              placeholder="Title"
                              value={draft.title}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [a.id]: {
                                    ...prev[a.id],
                                    title: e.target.value,
                                  },
                                }))
                              }
                            />

                            <textarea
                              rows={4}
                              className="
                                w-full border border-blue-100 rounded-2xl px-3 py-2
                                text-sm text-black
                                focus:outline-none focus:ring-2 focus:ring-blue-600
                              "
                              placeholder="Message"
                              value={draft.body}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [a.id]: {
                                    ...prev[a.id],
                                    body: e.target.value,
                                  },
                                }))
                              }
                            />

                            <div className="flex items-center justify-between gap-3">
                              <label className="inline-flex cursor-pointer items-center gap-2 ml-2 text-sm font-medium text-blue-700 select-none">
                                <input
                                  type="checkbox"
                                  checked={!!draft.pinned}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [a.id]: {
                                        ...prev[a.id],
                                        pinned: e.target.checked,
                                      },
                                    }))
                                  }
                                  className="peer sr-only"
                                />

                                <div className="flex h-4 w-4 items-center justify-center rounded-3xl bg-blue-50 border border-slate-200">
                                  <Check
                                    size={12}
                                    weight="bold"
                                    className={`text-blue-500 transition-opacity ${
                                      draft.pinned ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                </div>

                                <span>Pin this announcement</span>
                              </label>

                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="
                                    !inline-flex !items-center !gap-1 !rounded-full
                                    !border !border-blue-100 !bg-white
                                    !px-3 !h-8 !min-h-0
                                    !text-xs !font-semibold !text-blue-500 !transition
                                  "
                                  disabled={isSaving}
                                  title="Cancel"
                                >
                                  Cancel
                                </button>

                                <button
                                  type="button"
                                  onClick={() => saveRow(a)}
                                  className="
                                    !inline-flex !items-center !gap-1 !rounded-full !bg-blue-600
                                    !px-3 !h-8 !min-h-0
                                    !text-xs !font-semibold !text-white !transition
                                    disabled:!opacity-50
                                  "
                                  disabled={isSaving}
                                  title="Save"
                                >
                                  {isSaving ? "Saving…" : "Save"}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {isAdmin && !isEditing && (
                        <div className="flex shrink-0 pt-1.5 items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => startEdit(a)}
                            className="
                              !inline-flex !items-center !gap-1 !rounded-full
                              !border !border-blue-100
                              !bg-blue-100 !px-3 !h-6 !min-h-0
                              !text-xs !font-medium !text-blue-500 !transition
                            "
                            title="Edit"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => deleteRow(a)}
                            disabled={isDeleting}
                            className={`!inline-flex !items-center !gap-1 !rounded-full !px-3 !h-6 !min-h-0 !text-xs !font-medium !transition ${
                              isDeleting
                                ? "!bg-red-100 !text-red-700 !opacity-75 !cursor-not-allowed"
                                : "!bg-red-50 !text-red-700 hover:!bg-red-100"
                            }`}
                            title="Delete"
                          >
                            {isDeleting ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      )}
                    </div>

                    {!isEditing && body && (
                      <div className="mt-1 text-sm leading-5 text-blue-900/75 whitespace-pre-wrap break-words">
                        {body}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-end mt-4">
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate(`/circles/${id}/announcements/new`)}
              className="
                !ml-auto !inline-flex !h-6 !min-h-0
                !items-center !justify-center !rounded-full
                !bg-white !px-3 !text-sm !font-medium !text-blue-700 !ring-2 !ring-blue-700 !transition
              "
            >
              Create
            </button>
          )}
        </div>
      </main>
    </div>
  );
}